import React, { ComponentType, CSSProperties, ReactNode } from 'react';

import styled from 'styled-components';

import Tooltip from '@components/ui/Tooltip/next';
import CustomIcon from '@components/utils/CustomIcon';
import { PaletteColor } from '@utils/types';

export interface IconLabelProps {
  text: string;
  icon?: ComponentType<React.PropsWithChildren<unknown>>;
  iconLocation?: 'left' | 'right';
  iconSize?: number;
  iconColor?: PaletteColor;
  backgroundColor?: PaletteColor;
  width?: number;
  tooltipText?: string | ReactNode;
  color?: PaletteColor;
  textStyle?: CSSProperties;
  className?: string;
  description?: string;
  roomCard?: boolean;
  badgeText?: string;
}

const Wrapper = styled.div<{
  backgroundColor: PaletteColor;
  width?: number;
  iconLocation?: 'right' | 'left';
}>`
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
  height: auto;
  display: flex;
  gap: 4px;
  flex-direction: ${({ iconLocation }) =>
    iconLocation === 'right' ? 'row-reverse' : 'row'};
  align-items: center;

  & li {
    list-style: none !important;
  }

  justify-content: space-between;
  padding: ${({ iconLocation }) => (iconLocation === 'right' ? '6px' : '4px')};
  border-radius: 4px;
  background-color: ${({ backgroundColor, theme }) =>
    theme.palette[backgroundColor as PaletteColor]};

  .markdown-body li {
    list-style: disc !important;
    margin-left: 20px; /* Girinti ekleyelim */
  }

  .markdown-body li li {
    list-style: circle !important;
    margin-left: 20px;
  }
`;

const Text = styled.span<{ color: PaletteColor; iconLocation: string }>`
  font-size: 12px;
  font-weight: bold;
  color: ${({ color, theme }) => theme.palette[color as PaletteColor]};
`;
const Animation = styled.div`
  height: 1.2em;
  line-height: 1.2em;
  position: relative;
  overflow: hidden;
  width: 8em;
  > span {
    font-size: 12px;
    position: absolute;
    animation: multi 5s infinite;
    font-weight: 600;
  }

  @keyframes multi {
    0% {
      top: 0;
    }
    25% {
      top: -1.2em;
    }
    50% {
      margin-top: -0;
    }
    75% {
      margin-top: -0;
    }
    100% {
      margin-top: 0;
    }
  }
`;
const Animations = styled.div`
  height: 1.2em;
  line-height: 1.2em;
  position: relative;
  overflow: hidden;
  width: 8em;
  > span {
    font-size: 12px;
    position: absolute;
    animation: slide 6s infinite;
    font-weight: 600;
  }

  @keyframes slide {
    0% {
      top: 0;
    }
    25% {
      top: -1.2em;
    }
    50% {
      top: -2.4em;
    }
    75% {
      margin-top: -0;
    }
    100% {
      margin-top: 0;
    }
  }
`;

const NotAnimation = styled.div`
  height: 1.2em;
  line-height: 1.2em;
`;
const CardText = styled.span`
  margin: 0;
`;

const IconWrapper = styled.div<{ right: boolean; hasPointer: boolean }>`
  cursor: ${({ hasPointer }) => (hasPointer ? 'pointer' : 'auto')};
`;

const IconLabel = ({
  text,
  color = 'black',
  width,
  backgroundColor = 'white',
  textStyle,
  tooltipText,
  icon,
  iconLocation = 'left',
  iconSize = 18,
  iconColor,
  className,
  description,
  roomCard,
  badgeText,
}: IconLabelProps) => (
  <Tooltip
    getPopupContainer={(node) => node}
    overlayInnerStyle={{
      width: 'max-content',
      maxWidth: '350px',
      lineHeight: 1.43,
      padding: '12px 15px 11px',
      whiteSpace: 'normal',
      textAlign: 'left',
    }}
    placement='top'
    title={tooltipText}
  >
    <Wrapper
      width={width}
      className={className}
      backgroundColor={backgroundColor}
      iconLocation={iconLocation}
      style={{ cursor: tooltipText ? 'pointer' : 'auto' }}
    >
      <IconWrapper
        hasPointer={Boolean(tooltipText)}
        right={iconLocation === 'right'}
      >
        <CustomIcon fontSize={iconSize} icon={icon} paletteColor={iconColor} />
      </IconWrapper>

      {description && badgeText ? (
        <Text iconLocation={iconLocation} style={textStyle} color={color}>
          <Animations>
            <span>
              {badgeText}
              <br />
              {text}
              <br />
              {description}
            </span>
          </Animations>
        </Text>
      ) : description && !badgeText ? (
        <Text iconLocation={iconLocation} style={textStyle} color={color}>
          <Animation>
            <span>
              {text}
              <br />
              {description}
            </span>
          </Animation>
        </Text>
      ) : !description && badgeText ? (
        <Text iconLocation={iconLocation} style={textStyle} color={color}>
          <Animation>
            <span>
              {badgeText}
              <br />
              {text}
            </span>
          </Animation>
        </Text>
      ) : (
        <Text iconLocation={iconLocation} style={textStyle} color={color}>
          {roomCard ? (
            <CardText>{text}</CardText>
          ) : (
            <NotAnimation>{text}</NotAnimation>
          )}
        </Text>
      )}
    </Wrapper>
  </Tooltip>
);

export default IconLabel;
